import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { BLOG_POSTS } from "../data/blog";
import { FadeUp, SectionTag } from "../components/Motion";
import { DotStrip } from "../components/Brand";

const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold text-nexa-bone">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return (
        <a key={i} href={link[2]} target={link[2].startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="link-line text-nexa-red">
          {link[1]}
        </a>
      );
    }
    return part;
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const post = BLOG_POSTS[idx];

  if (!post) {
    return (
      <div className="mx-auto max-w-[1600px] px-5 py-40 md:px-10" data-testid="post-not-found">
        <h1 className="font-display text-5xl uppercase text-nexa-bone">That note never made it out of the den.</h1>
        <Link to="/blog" className="link-line mt-6 inline-block font-mono text-xs uppercase tracking-[0.2em] text-nexa-red" data-testid="post-not-found-back">
          Back to Field Notes
        </Link>
      </div>
    );
  }

  const prev = BLOG_POSTS[idx - 1];
  const next = BLOG_POSTS[idx + 1];

  return (
    <>
      <section className="relative overflow-hidden border-b border-white/10" data-testid="blog-post-hero">
        <div className="noise-overlay" />
        <div className="mx-auto max-w-[1600px] px-5 pb-14 pt-28 md:px-10 md:pt-36">
          <Link to="/blog" className="link-line inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-nexa-bone/60" data-testid="blog-back-link">
            <ArrowLeft size={14} /> Field Notes
          </Link>
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-nexa-red">
            {post.category} · {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · {post.readTime} read
          </p>
          <h1 className="mt-4 max-w-5xl font-display text-4xl uppercase leading-[0.98] tracking-tight text-nexa-bone sm:text-5xl lg:text-7xl">
            {post.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-nexa-bone/60 md:text-lg">{post.excerpt}</p>
        </div>
      </section>

      <article className="mx-auto grid max-w-[1600px] gap-8 px-5 py-16 md:grid-cols-12 md:px-10" data-testid="blog-post-body">
        <div className="md:col-span-3">
          <SectionTag>Nexa Studio — Kolkata</SectionTag>
          <p className="mt-4 text-sm leading-relaxed text-nexa-bone/50">
            An attack unit for brands, built on 12 years of Myndroot's battle tested portfolio.
          </p>
        </div>
        <div className="max-w-3xl space-y-6 md:col-span-9">
          {post.body.map((b, i) =>
            b.t === "h" ? (
              <h2 key={i} className="pt-4 font-display text-2xl uppercase tracking-tight text-nexa-bone md:text-3xl">
                {renderInline(b.x)}
              </h2>
            ) : b.t === "li" ? (
              <div key={i} className="flex items-baseline gap-3 text-base leading-relaxed text-nexa-bone/70 md:text-lg">
                <span className="h-2 w-2 shrink-0 translate-y-[-2px] bg-nexa-red" />
                <span>{renderInline(b.x)}</span>
              </div>
            ) : (
              <p key={i} className="text-base leading-relaxed text-nexa-bone/70 md:text-lg">
                {renderInline(b.x)}
              </p>
            )
          )}
          <FadeUp>
            <div className="mt-6 border-l-2 border-nexa-red pl-6" data-testid="blog-cta">
              <p className="font-display text-2xl uppercase leading-tight text-nexa-bone">
                Want this aimed at your brand?
              </p>
              <Link to="/contact" className="group mt-4 inline-flex items-center gap-3 bg-nexa-red px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-transform duration-200 hover:-translate-y-1 hover:bg-nexa-bone hover:text-nexa-black" data-testid="blog-cta-button">
                Brief the den <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </article>

      <DotStrip className="text-nexa-red/70" />

      <section className="mx-auto grid max-w-[1600px] md:grid-cols-2" data-testid="blog-post-nav">
        <div className="border-b border-white/10 md:border-b-0 md:border-r">
          {prev && (
            <Link to={`/blog/${prev.slug}`} className="group block px-5 py-10 md:px-10" data-testid="prev-post-link">
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-nexa-bone/40">
                <ArrowLeft size={14} /> Previous note
              </span>
              <span className="mt-2 block font-display text-2xl uppercase leading-tight text-nexa-bone transition-colors duration-300 group-hover:text-nexa-red">
                {prev.title}
              </span>
            </Link>
          )}
        </div>
        <div>
          {next && (
            <Link to={`/blog/${next.slug}`} className="group block px-5 py-10 text-right md:px-10" data-testid="next-post-link">
              <span className="flex items-center justify-end gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-nexa-bone/40">
                Next note <ArrowRight size={14} />
              </span>
              <span className="mt-2 block font-display text-2xl uppercase leading-tight text-nexa-bone transition-colors duration-300 group-hover:text-nexa-red">
                {next.title}
              </span>
            </Link>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogPost;
